import { useCallback, useEffect, useState } from "react";
import api, { errMsg } from "@/lib/api";
import { toast } from "sonner";
import { Heart, Check } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

// Saved-card keys ("vendor:ID" / "marketplace:ID") for the signed-in parent.
export function useSavedItems() {
  const { user } = useAuth();
  const isParent = user?.role === "parent";
  const [saved, setSaved] = useState(new Set());
  useEffect(() => {
    if (!isParent) return;
    api.get("/saved-items")
      .then((r) => setSaved(new Set(r.data.map((i) => `${i.item_type}:${i.item_id}`))))
      .catch(() => {});
  }, [isParent]);
  const add = useCallback(async (itemType, itemId) => {
    try {
      await api.post("/saved-items", { item_type: itemType, item_id: itemId });
      setSaved((s) => new Set(s).add(`${itemType}:${itemId}`));
      toast.success("Added to your list");
    } catch (e) {
      toast.error(errMsg(e, "Could not add to your list"));
    }
  }, []);
  return { isParent, saved, add };
}

export function SaveToListButton({ itemType, itemId, saved, add }) {
  const isSaved = saved.has(`${itemType}:${itemId}`);
  return (
    <button
      type="button"
      onClick={(e) => { e.preventDefault(); e.stopPropagation(); if (!isSaved) add(itemType, itemId); }}
      disabled={isSaved}
      className={`mt-3 inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-full border transition-colors ${isSaved ? "bg-sage-light text-sage-dark border-sage/30 cursor-default" : "bg-white text-terracotta-dark border-terracotta/40 hover:bg-terracotta-light"}`}
      data-testid={`save-to-list-${itemType}-${itemId}`}
    >
      {isSaved ? <><Check className="w-3.5 h-3.5" /> Added</> : <><Heart className="w-3.5 h-3.5" /> Add to my List</>}
    </button>
  );
}
